// assets/pdc-cart-guard.js
// Keeps delivery-option/upgrade consistency in the cart WITHOUT overriding window.fetch
// and without indefinitely blocking checkout. Normalises on: cart-page load,
// remove/update clicks, and (time-boxed) right before checkout.

(function () {
  'use strict';

  var SPEED_OPTIONS = { priority: true, rush: true, sea: true };
  var SPEED_LABELS  = { standard: 'Standard', priority: 'Priority Build', rush: 'Rush Build', sea: 'Sea Freight' };

  // ── Standard (free) lead time ────────────────────────────────────────────────
  // Preferred source is the item's own _pdc_standard_lead_min_days, captured from the
  // live section settings when it was added to cart (see delivery-calendar.js). Falls
  // back to a site-wide override, then to 41 (delivery-calendar.js default) for items
  // added before this property existed.
  function standardLeadMinDays(props) {
    var v = props ? parseInt(props._pdc_standard_lead_min_days, 10) : NaN;
    if (!isNaN(v)) return v;
    v = parseInt(window.PDC_STANDARD_LEAD_MIN_DAYS, 10);
    return isNaN(v) ? 41 : v;
  }
  var MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  var MONTH_INDEX = { jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11 };
  function startOfDay(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
  function isWeekend(d) { var day = d.getDay(); return day === 0 || day === 6; }
  function formatCustomerDate(d) { return d.getDate() + ' ' + MONTH_ABBR[d.getMonth()] + ' ' + d.getFullYear(); }
  function parseCustomerDate(str) {
    if (!str) return null;
    var m = String(str).trim().match(/^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/);
    if (!m) return null;
    var mon = MONTH_INDEX[m[2].slice(0, 3).toLowerCase()];
    if (mon === undefined) return null;
    return new Date(parseInt(m[3], 10), mon, parseInt(m[1], 10));
  }
  // Earliest valid free/standard delivery date as of *now* (skips weekends),
  // mirroring delivery-calendar.js getFirstDateForWindow('standard'). Uses the item's
  // own captured lead offset so it matches whatever the product was configured with.
  function currentStandardDate(props) {
    var d = startOfDay(new Date());
    d.setDate(d.getDate() + standardLeadMinDays(props));
    while (isWeekend(d)) d.setDate(d.getDate() + 1);
    return d;
  }
  function isDeliveryItem(props) {
    return props['Delivery date'] !== undefined ||
           props._pdc_delivery_option !== undefined ||
           props._pdc_booking_id !== undefined;
  }

  function cartUrl(path) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    return root.replace(/\/$/, '') + path;
  }
  function getProps(item) { return item && item.properties && typeof item.properties === 'object' ? Object.assign({}, item.properties) : {}; }
  function normalizeDeliveryOption(value) {
    value = String(value || '').toLowerCase();
    if (value.indexOf('priority') !== -1) return 'priority';
    if (value.indexOf('rush') !== -1) return 'rush';
    if (value.indexOf('sea') !== -1) return 'sea';
    if (value.indexOf('standard') !== -1) return 'standard';
    return value;
  }
  function getDeliveryOption(props) {
    return normalizeDeliveryOption(props._pdc_delivery_option || props.delivery_option || props.delivery_window || props['Delivery option'] || '');
  }
  function getBookingId(props) { return String(props._pdc_booking_id || ''); }
  function isSpeedUpgrade(item) {
    var props = getProps(item);
    return props._pdc_item_type === 'production_speed_upgrade' || props.type === 'production_speed_upgrade' || props.Type === 'production_speed_upgrade';
  }
  function getUpgradeWindow(props) { return normalizeDeliveryOption(props._pdc_production_window || props.production_window || props.delivery_option || ''); }
  function upgradeMatchesMain(upgradeItem, mainItem, wantedWindow) {
    var up = getProps(upgradeItem), mainProps = getProps(mainItem);
    if (!isSpeedUpgrade(upgradeItem)) return false;
    var upWindow = getUpgradeWindow(up);
    if (upWindow && upWindow !== wantedWindow) return false;
    var mBid = getBookingId(mainProps), uBid = getBookingId(up);
    if (mBid && uBid && mBid === uBid) return true;
    var appliedVariant = String(up._pdc_upgrade_for || '');
    return appliedVariant && appliedVariant === String(mainItem.variant_id);
  }
  function upgradeHasMain(upgradeItem, cartItems) {
    var up = getProps(upgradeItem), uBid = getBookingId(up), uFor = String(up._pdc_upgrade_for || '');
    return cartItems.some(function (item) {
      if (item === upgradeItem || isSpeedUpgrade(item)) return false;
      var props = getProps(item), mBid = getBookingId(props);
      if (uBid && mBid && uBid === mBid) return true;
      return uFor && uFor === String(item.variant_id);
    });
  }
  function fetchCart() { return fetch(cartUrl('/cart.js'), { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(function (r) { return r.json(); }); }
  function changeLine(lineNumber, quantity, properties) {
    var payload = { line: lineNumber, quantity: quantity };
    if (properties) payload.properties = properties;
    return fetch(cartUrl('/cart/change.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, credentials: 'same-origin', body: JSON.stringify(payload) });
  }

  function normalizeCart(options) {
    options = options || {};
    var shouldReload = !!options.reload;
    return fetchCart().then(function (cart) {
      if (!cart || !cart.items || !cart.items.length) return false;

      var changes = [];
      cart.items.forEach(function (item, index) {
        var props = getProps(item);

        // Speed-upgrade line: remove it if its main item is no longer in the cart.
        if (isSpeedUpgrade(item)) {
          if (!upgradeHasMain(item, cart.items)) changes.push({ action: 'remove', line: index + 1 });
          return;
        }

        // Only ever touch items that belong to the delivery-date system.
        if (!isDeliveryItem(props)) return;

        // Current earliest free/standard date for THIS item's configured lead time.
        var stdDate = currentStandardDate(props);
        var stdTs = stdDate.getTime();
        var stdFormatted = formatCustomerDate(stdDate);

        var deliveryOption = getDeliveryOption(props);
        var newProps = null;

        // (1) On a paid speed option but the matching upgrade line is gone → downgrade.
        if (SPEED_OPTIONS[deliveryOption]) {
          var hasMatchingUpgrade = cart.items.some(function (o) { return upgradeMatchesMain(o, item, deliveryOption); });
          if (!hasMatchingUpgrade) {
            newProps = Object.assign({}, props);
            newProps['Delivery option'] = SPEED_LABELS.standard;
            newProps._pdc_delivery_option = 'standard';
            newProps._pdc_downgraded_from = deliveryOption;
            newProps._pdc_downgraded_from_label = SPEED_LABELS[deliveryOption] || deliveryOption;
            newProps._pdc_downgraded_reason = 'Production Speed Upgrade removed';
            if (newProps.delivery_option !== undefined) newProps.delivery_option = 'standard';
            deliveryOption = 'standard'; // fall through to the date refresh below
          }
        }

        // (2) No paid upgrade (standard): never let a stale/past date sit in the cart.
        //     If the stored date is earlier than today's earliest free-delivery date,
        //     roll it forward to the current standard date. A date the customer chose
        //     further out (still >= today's minimum) is left untouched.
        if (!SPEED_OPTIONS[deliveryOption]) {
          var storedStr = (newProps && newProps['Delivery date']) || props['Delivery date'];
          var storedDate = parseCustomerDate(storedStr);
          if (!storedDate || storedDate.getTime() < stdTs) {
            newProps = newProps || Object.assign({}, props);
            newProps['Delivery date'] = stdFormatted;
            newProps['Delivery option'] = newProps['Delivery option'] || SPEED_LABELS.standard;
            newProps._pdc_delivery_option = 'standard';
            newProps._pdc_standard_delivery_date_formatted = stdFormatted;
            newProps._pdc_date_refreshed = stdFormatted;
            if (newProps.delivery_date !== undefined) newProps.delivery_date = stdFormatted;
          }
        }

        if (newProps) changes.push({ action: 'update', line: index + 1, quantity: item.quantity, properties: newProps });
      });
      if (!changes.length) return false;
      changes.sort(function (a, b) { return b.line - a.line; });
      var chain = Promise.resolve();
      changes.forEach(function (change) {
        chain = chain.then(function () { return change.action === 'remove' ? changeLine(change.line, 0) : changeLine(change.line, change.quantity, change.properties); });
      });
      return chain.then(function () { if (shouldReload) window.location.reload(); return true; });
    });
  }

  function isCartPage() { return /\/cart\/?$/.test(window.location.pathname); }

  // Never let normalisation block the UI for more than `ms`.
  function withTimeout(promise, ms) {
    return new Promise(function (resolve) {
      var done = false;
      var t = setTimeout(function () { if (!done) { done = true; resolve(); } }, ms);
      function finish() { if (!done) { done = true; clearTimeout(t); resolve(); } }
      promise.then(finish, finish);
    });
  }

  // Public hook (kept for compatibility)
  window.pdcNormalizeCartDeliveryDates = normalizeCart;

  // On the cart page: normalise once; reload only if something actually changed.
  document.addEventListener('DOMContentLoaded', function () {
    if (!isCartPage()) return;
    normalizeCart({ reload: true }).catch(function (e) { console.warn('PDC cart guard failed:', e); });
  });

  // Right before checkout: normalise, but time-boxed so a slow/failed request never blocks payment.
  document.addEventListener('click', function (event) {
    var checkoutButton = event.target.closest('button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]');
    if (!checkoutButton) return;
    // Do not interfere with accelerated (Shop Pay etc.) dynamic checkout buttons.
    if (checkoutButton.closest('.shopify-payment-button, [data-shopify="dynamic-checkout-cart"]')) return;
    event.preventDefault();
    event.stopPropagation();
    withTimeout(normalizeCart({ reload: false }).catch(function () {}), 1200)
      .then(function () { window.location.href = cartUrl('/checkout'); });
  }, true);

  // Debounced re-normalise after a remove/update click (covers manual cart edits).
  var scheduled = null;
  function scheduleNormalize() {
    clearTimeout(scheduled);
    scheduled = setTimeout(function () {
      normalizeCart({ reload: isCartPage() }).catch(function (e) { console.warn('PDC scheduled cart guard failed:', e); });
    }, 650);
  }
  document.addEventListener('click', function (event) {
    var el = event.target.closest('cart-remove-button, .cart-remove-button, [href*="/cart/change"], [data-cart-remove], [data-remove], button[name="updates[]"], input[name="updates[]"]');
    if (el) scheduleNormalize();
  }, true);

  // Reliable cart-change detection WITHOUT overriding window.fetch: watch the cart
  // containers for DOM changes. This fires whenever a line is removed or its quantity
  // changes (Dawn re-renders the cart section), however it was triggered — including
  // removing the speed-upgrade line, which then downgrades the main item to Standard.
  function watchCartDom() {
    var targets = document.querySelectorAll('cart-items, cart-drawer-items, cart-drawer, #main-cart-items, .cart-items, .cart-drawer');
    if (!targets.length) return;
    var mo = new MutationObserver(function () { scheduleNormalize(); });
    targets.forEach(function (t) {
      try { mo.observe(t, { childList: true, subtree: true }); } catch (e) {}
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchCartDom, { once: true });
  } else {
    watchCartDom();
  }

  // NOTE: the previous version monkey-patched window.fetch to catch cart changes.
  // That global override was removed for stability; the MutationObserver above plus the
  // remove/update clicks, cart-page load, and pre-checkout pass cover the real flows.
})();